import type { Project } from '@/stores/sequencerStore'
import type { Sample } from '@/hooks/useSampleUpload'

/**
 * Walk a project's active pattern and return the sample IDs +
 * presigned play URLs the playback path will need.
 *
 * Covers every pad kind that reaches for a decoded AudioBuffer at
 * trigger time:
 *
 *  - `kind: 'sample'`
 *  - `kind: 'granular'` (uses `GrainPlayer`, which decodes lazily;
 *    included so the first hit isn't stuttered)
 *  - `kind: 'instrument'` with a `sampleId` (the Sampler instrument
 *    — matches the `triggerInstrument` buffer-injection path)
 *
 * Synth pads, pure-MIDI pads, and empty slots don't need anything
 * and are skipped. Returns a deduped list so callers can run
 * `Promise.all(ids.map(preloadSample))` without double-fetching.
 */
export interface RequiredSample {
  sampleId: string
  playUrl: string
  name?: string
}

export function collectRequiredSampleIds(
  projects: Project | Project[],
  samples: Sample[],
): RequiredSample[] {
  const sampleById = new Map(samples.map(s => [s.id, s]))
  const seen = new Set<string>()
  const out: RequiredSample[] = []
  const projectList = Array.isArray(projects) ? projects : [projects]
  for (const project of projectList) {
    const padDefs = project.padDefs ?? {}
    for (const def of Object.values(padDefs)) {
      let sampleId: string | undefined
      if (def.kind === 'sample' || def.kind === 'granular') {
        sampleId = def.sampleId
      } else if (def.kind === 'instrument' && def.sampleId) {
        sampleId = def.sampleId
      }
      if (!sampleId) continue
      if (seen.has(sampleId)) continue
      seen.add(sampleId)
      const meta = sampleById.get(sampleId)
      if (!meta?.playUrl) continue
      out.push({ sampleId, playUrl: meta.playUrl, name: meta.name })
    }
  }
  return out
}
